"use client";

import { useTabScopedContext } from "../__chunks/BSRZWVZO.js";
import { CompositeItem, useCompositeItem } from "../__chunks/NSB34BGG.js";
import "../__chunks/SZHY72KI.js";
import "../__chunks/ZHUQ44XA.js";
import "../__chunks/5VQZOHHZ.js";
import "../__chunks/APGXEJ2Q.js";
import "../__chunks/G4PVPWEW.js";
import "../__chunks/Z4WWVOIF.js";
import "../__chunks/SWN3JYXT.js";
import { createElement, createHook, forwardRef, memo } from "../__chunks/7FZLUSKW.js";
import { useStoreState } from "../__chunks/CTKPPFRS.js";
import { useEvent, useId } from "../__chunks/HNHAUIIY.js";
import "../__chunks/SK3NAZA3.js";
import { __objRest, __spreadProps, __spreadValues } from "../__chunks/3YLGPPWQ.js";

// src/tab/tab.tsx
import { disabledFromProps, invariant } from "@ariakit/core/utils/misc";
import { useCallback } from "react";
import { jsx } from "react/jsx-runtime";
var TagName = "button";
var useTab = createHook(function useTab2(_a) {
  var _b = _a,
    store = _b.store,
    getItemProp = _b.getItem,
    props = __objRest(_b, ["store", "getItem"]);
  var _a2;
  var context = useTabScopedContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "Tab must be wrapped in a TabList component.");
  var defaultId = useId();
  var id = props.id || defaultId;
  var dimmed = disabledFromProps(props);
  var getItem = useCallback(function (item) {
    var nextItem = __spreadProps(__spreadValues({}, item), {
      dimmed: dimmed
    });
    if (getItemProp) {
      return getItemProp(nextItem);
    }
    return nextItem;
  }, [dimmed, getItemProp]);
  var onClickProp = props.onClick;
  var onClick = useEvent(function (event) {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    store == null ? void 0 : store.setSelectedId(id);
  });
  var panelId = store.panels.useState(function (state) {
    var _a3;
    return (_a3 = state.items.find(function (item) {
      return item.tabId === id;
    })) == null ? void 0 : _a3.id;
  });
  var shouldRegisterItem = defaultId ? props.shouldRegisterItem : false;
  var isActive = store.useState(function (state) {
    return !!id && state.activeId === id;
  });
  var selected = store.useState(function (state) {
    return !!id && state.selectedId === id;
  });
  var hasActiveItem = store.useState(function (state) {
    return !!store.item(state.activeId);
  });
  var canRegisterComposedItem = isActive || selected && !hasActiveItem;
  var accessibleWhenDisabled = selected || ((_a2 = props.accessibleWhenDisabled) != null ? _a2 : true);
  var isWithinVirtualFocusComposite = useStoreState(store.combobox || store.composite, "virtualFocus");
  if (isWithinVirtualFocusComposite) {
    props = __spreadProps(__spreadValues({}, props), {
      tabIndex: -1
    });
  }
  props = __spreadProps(__spreadValues({
    id: id,
    role: "tab",
    "aria-selected": selected,
    "aria-controls": panelId || void 0
  }, props), {
    onClick: onClick
  });
  if (store.composite) {
    var defaultProps = {
      id: id,
      accessibleWhenDisabled: accessibleWhenDisabled,
      store: store.composite,
      shouldRegisterItem: canRegisterComposedItem && shouldRegisterItem,
      rowId: props.rowId,
      render: props.render
    };
    props = __spreadProps(__spreadValues({}, props), {
      render: /* @__PURE__ */jsx(CompositeItem, __spreadProps(__spreadValues({}, defaultProps), {
        render: store.combobox && store.composite !== store.combobox ? /* @__PURE__ */jsx(CompositeItem, __spreadProps(__spreadValues({}, defaultProps), {
          store: store.combobox
        })) : defaultProps.render
      }))
    });
  }
  props = useCompositeItem(__spreadProps(__spreadValues({
    store: store
  }, props), {
    accessibleWhenDisabled: accessibleWhenDisabled,
    getItem: getItem,
    shouldRegisterItem: shouldRegisterItem
  }));
  return props;
});
var Tab = memo(forwardRef(function Tab2(props) {
  var htmlProps = useTab(props);
  return createElement(TagName, htmlProps);
}));
export { Tab, useTab };